import React, { useState } from 'react';
import { Card, CardContent } from '../../../../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../../components/ui/table';
import { ArrowLeft, FileText, Edit, Trash2, Upload, Scissors, Split, Cable, Eye, EyeIcon } from 'lucide-react';
import { cn } from '../../../../../../lib/utils';
import { Materials, MaterialData } from '../../../../types/materials';
import { Button } from '../../../../../../components/ui/button';
import { DeleteConfirmationDialog, DeleteItem } from '../../../../../../components/ui/DeleteConfirmationDialog';
import { Pagination } from '../../../../../../components/ui/Pagination';
import { SortableTableHead } from '../../../../../../components/ui/SortableTableHead';
import { useTableSort } from '../../../../../../hooks/useTableSort';
import { deleteMaterials, DeleteMaterialDocument } from '../../../../../../redux/Project/materialSaga';
import { UpdateMaterialDialog } from '../UpdateMaterialDialog';
import { DocumentDownloadModal } from '../../specifications/DocumentDownloadModal';
import { MaterialDocumentUploadModal } from '../MaterialDocumentUploadModal';
import { DocumentTag } from '../../../../../../features/project/types';
import { PipeFilters } from './Filters/PipeFilters';
import { usePipeFilters } from './Filters/hooks/usePipeFilters';
import { convertSetupValue, convertValueFromInches } from '../../../../../../utils/unitUtils';
import { PipeSegmentationModal } from './PipeSegmentationModal';
import { convertInchesToFeet, formatDate } from '../../../../../../utils/unitUtils';
import PipeFfitter from '../PipeFfitter';
import { PipeDataUploadModal } from '../PipeFfitter/PipeDataUploadModal';
import { PipeMaterialReportModal } from '../pipeMillReport/PipeMaterialReportModal';
import { pipeMillReportApi } from '../../../../../../redux/Project/pipeMillReport/api';

function getMaterialStatusDisplay(material: MaterialData): string {
  const extra = material.materialExtraDetails as { materialStatus?: string } | undefined;
  const nested = (material.details as any)?.material_extra_details;
  return (
    extra?.materialStatus ??
    nested?.materialStatus ??
    material.materialStatus ??
    '-'
  );
}

type HeatReportStatus = 'PASS' | 'FAIL' | 'PENDING_REVIEW' | 'INCOMPLETE';

function normalizeHeatReportStatus(value: unknown): HeatReportStatus {
  const status = String(value || '').trim().toUpperCase().replace(/\s+/g, '_');
  if (status === 'PASS') return 'PASS';
  if (status === 'FAIL') return 'FAIL';
  if (status === 'PENDING' || status === 'PENDING_REVIEW') return 'PENDING_REVIEW';
  return 'INCOMPLETE';
}

function combineHeatReportStatuses(statuses: HeatReportStatus[]): HeatReportStatus {
  if (!statuses.length) return 'PENDING_REVIEW';
  if (statuses.includes('FAIL')) return 'FAIL';
  if (statuses.includes('PENDING_REVIEW')) return 'PENDING_REVIEW';
  if (statuses.includes('INCOMPLETE')) return 'INCOMPLETE';
  return 'PASS';
}

function getPipeLotNumber(material: MaterialData): string {
  const details = material.details || {};
  return (
    details.pipeLotNumber ||
    details.pipeExtraDetails?.pipeLotNumber ||
    details.pipeExtraDetails?.pipeData?.lotId ||
    details.pipeExtraDetails?.lotId ||
    '-'
  );
}

function getReportType(report: any): string {
  return String(report?.reportType || report?.report_type || '').toUpperCase();
}

function getReportLotNumber(report: any): string {
  const extra = report?.extraDetails || report?.extra_details || {};
  return String(extra.pipeLotNumber || extra.lotId || extra.lot_id || extra.lotNumber || extra.lot_number || '');
}

function getReportBatchNumber(report: any): string {
  const extra = report?.extraDetails || report?.extra_details || {};
  return String(extra.batchId || extra.batch_id || extra.batchNumber || extra.batch_number || extra.furnaceBatch || extra.lotNumber || '');
}

function getStatusPillClass(status: HeatReportStatus): string {
  if (status === 'PASS') return 'border-green-200 bg-green-50 text-green-700';
  if (status === 'FAIL') return 'border-red-200 bg-red-50 text-red-700';
  if (status === 'PENDING_REVIEW') return 'border-amber-200 bg-amber-50 text-amber-700';
  return 'border-gray-200 bg-gray-50 text-gray-700';
}

const LOT_LEVEL_REPORTS = [
  { key: 'TENSILE', label: 'Tensile' },
  { key: 'CVN', label: 'CVN' },
  { key: 'HARDNESS', label: 'Hardness' },
  { key: 'FLATTENING', label: 'Flattening' },
  { key: 'BEND', label: 'Bend' },
];

const GENERATED_PIPE_COUNT = 1000;

export function makePipes(): MaterialData[] {
  const list: MaterialData[] = [];
  const heats = [
    'J4K-5270',
    'J4K-5271',
    'J4K-5272',
    'J4K-5273',
    'J4K-5274',
    'J4K-5275',
    'J4K-5276',
    'J4K-5277',
    'J4K-5278',
    'J4K-5279',
    'J4K-5280',
    'J4K-5281',
  ];

  for (let index = 0; index < GENERATED_PIPE_COUNT; index++) {
    const pipeSeq = 1101 + index;
    const r = Math.sin(pipeSeq * 12.9898) * 43758.5453;
    const f = r - Math.floor(r);
    let clearance = 'PASS';

    if (f < 0.06) clearance = 'FAIL';
    else if (f < 0.16) clearance = 'PENDING_REVIEW';
    else if (f < 0.22) clearance = 'INCOMPLETE';

    const materialStatus = clearance === 'PASS' ? 'Available' : clearance === 'FAIL' ? 'Consumed' : 'Shipped to NDT';
    const heatIndex = index % heats.length;
    const heatNumber = heats[heatIndex];
    const lotNumber = `LOT-${200 + heatIndex}`;

    list.push({
      materialId: 900000 + pipeSeq,
      materialCategory: 'Pipe',
      materialStatus,
      materialDate: new Date(2026, index % 12, (index % 28) + 1).toISOString(),
      details: {
        pipeNRI: `NRI-U-${10000 + pipeSeq}`,
        pipeNo: `${30000 + pipeSeq}`,
        pipePipeId: `PIPE-${pipeSeq}`,
        pipeHeatNumber: heatNumber,
        pipeLength: 480 + (index % 40),
        pipeProjectStage: index % 5 === 0 ? 'PQT' : 'Production',
        pipeExtraDetails: {
          pipeLotNumber: lotNumber,
          pipeData: {
            lotId: lotNumber,
            sequence: pipeSeq,
            clearance,
            passing: Math.max(
              10,
              18 -
                (clearance === 'FAIL'
                  ? 3
                  : clearance === 'PENDING_REVIEW'
                    ? 2
                    : clearance === 'INCOMPLETE'
                      ? 4
                      : 0) -
                Math.floor(f * 3),
            ),
          },
        },
      },
      documents: [],
    });
  }

  return list;
}

interface PipeTabProps {
  /** Material category for this tab (Pipe, Bends, Flanges, Hydro, Torquing, Gauging, Flushing). */
  materialCategory?: string;
  materials: MaterialData[];
  selectedPipeSize: number;
  currentPage: number;
  materialsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  unit: string;
  refreshMaterials: () => void;
  onRowDelete?: (materialId: string | number, category: string) => Promise<void>;
  deletingRows?: Set<string>;
  documentTags: DocumentTag[];
  setDocumentTags: (any: any) => void;
  pipeSizesData: any;
  canEdit?: boolean;
  onOpenViewer?: (fileOrFiles: any | any[]) => void;
  project: any;
  isPipemillproject?: boolean;
  onReportsChanged?: () => void | Promise<void>;
  /** When true, skip heat summary drill-down and show the materials table directly (pump house table view). */
  forceTableView?: boolean;
}

export const PipeTab: React.FC<PipeTabProps> = React.memo(({
  materialCategory = 'Pipe',
  materials,
  selectedPipeSize,
  currentPage,
  materialsPerPage,
  onPageChange,
  onItemsPerPageChange,
  unit,
  refreshMaterials,
  onRowDelete,
  deletingRows,
  documentTags,
  setDocumentTags,
  pipeSizesData,
  canEdit = true,
  onOpenViewer,
  project,
  isPipemillproject = false,
  onReportsChanged,
  forceTableView = false,
}) => {
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [itemToUpdate, setItemToUpdate] = useState<MaterialData | null>(null);
  const [isDocumentModalVisible, setIsDocumentModalVisible] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialData | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isSegmentationModalOpen, setIsSegmentationModalOpen] = useState(false);
  const [selectedPipeForSegmentation, setSelectedPipeForSegmentation] = useState<MaterialData | null>(null);
  const [isUploadFilterModalOpen, setIsUploadFilterModalOpen] = useState(false);
  const [isPipeFitterOpen, setIsPipeFitterOpen] = useState(false);
  const [isPipeDataUploadModalOpen, setIsPipeDataUploadModalOpen] = useState(false);
  const [isPipeReportModalOpen, setIsPipeReportModalOpen] = useState(false);
  const [initialPipeReportType, setInitialPipeReportType] = useState<string | null>(null);
  const [isDocumentDeleteModalOpen, setIsDocumentDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);
  const [selectedHeatNumber, setSelectedHeatNumber] = useState<string | null>(null);
  const [pipeMillReports, setPipeMillReports] = useState<any[]>([]);
  const [isHeatSummaryLoading, setIsHeatSummaryLoading] = useState(false);
  const [heatSummarySearch, setHeatSummarySearch] = useState('');
  const [heatSummaryStatusFilter, setHeatSummaryStatusFilter] = useState<'all' | HeatReportStatus | 'NO_REPORTS'>('all');
  const generatedPipeMaterials = React.useMemo(() => isPipemillproject ? [] : [], []);
  const hasPipeFitter = Boolean(pipeSizesData?.pipeFitter);
  const materialsWithGeneratedPipes = React.useMemo(
    () => [...materials, ...generatedPipeMaterials],
    [materials, generatedPipeMaterials]
  );
  // Use the pipe filters hook


  const {
    searchQuery,
    setSearchQuery,
    filters,
    onFilterChange,
    uniquePipeNumbers,
    uniquePipeNRIs,
    uniquePipeProjectStages,
    uniqueHeatNumbers,
    uniqueOuterDiameters,
    uniqueWallThicknesses,
    uniqueProjectNames,
    filteredMaterials,
    hasActiveFilters,
    filteredCount,
    onResetFilters,
  } = usePipeFilters({ materials: materialsWithGeneratedPipes });

  const pipeMaterials = filteredMaterials.filter(
    (material: MaterialData) => material.materialCategory === materialCategory
  );

  // Add sorting functionality
  const { sortedData: sortedPipeMaterials, handleSort, getSortDirection } = useTableSort(pipeMaterials);
  const isBasePipeCategory = materialCategory === 'Pipe';
  const projectId = project?.projectId || pipeSizesData?.projectId;
  const pipeId = pipeSizesData?.pipeId || pipeSizesData?.pipe_id;

  const refreshPipeMillReports = React.useCallback(async () => {
    if (!isBasePipeCategory || !projectId || forceTableView) {
      setPipeMillReports([]);
      return;
    }

    setIsHeatSummaryLoading(true);
    try {
      const reports = await pipeMillReportApi.fetchReports({ projectId, pipeId });
      setPipeMillReports(Array.isArray(reports) ? reports : []);
    } catch {
      setPipeMillReports([]);
    } finally {
      setIsHeatSummaryLoading(false);
    }
  }, [isBasePipeCategory, pipeId, projectId, forceTableView]);

  React.useEffect(() => {
    let isMounted = true;
    const loadReports = async () => {
      if (!isMounted) return;
      await refreshPipeMillReports();
    };
    loadReports();
    return () => {
      isMounted = false;
    };
  }, [refreshPipeMillReports]);

  const handlePipeReportChanged = React.useCallback(async () => {
    await refreshPipeMillReports();
    await onReportsChanged?.();
  }, [onReportsChanged, refreshPipeMillReports]);

  React.useEffect(() => {
    setSelectedHeatNumber(null);
  }, [selectedPipeSize, materialCategory, forceTableView]);

  const heatSummaryRows = React.useMemo(() => {
    const reportsByHeat = pipeMillReports.reduce((acc: Record<string, any[]>, report: any) => {
      const heat = String(report.heatNumber || report.heat_number || 'UNASSIGNED');
      acc[heat] = [...(acc[heat] || []), report];
      return acc;
    }, {});

    const grouped = pipeMaterials.reduce((acc: Record<string, MaterialData[]>, material: MaterialData) => {
      const heat = String(material.details?.pipeHeatNumber || 'UNASSIGNED');
      acc[heat] = [...(acc[heat] || []), material];
      return acc;
    }, {});

    return Object.entries(grouped)
      .map(([heatNumber, heatPipes]) => {
        const lots = new Set(heatPipes.map(getPipeLotNumber).filter((lot) => lot && lot !== '-'));
        const reports = reportsByHeat[heatNumber] || [];
        const statuses = reports.map((report) => normalizeHeatReportStatus(report.overallStatus || report.overall_status));
        const overallStatus = combineHeatReportStatuses(statuses);
        const passCount = statuses.filter((status) => status === 'PASS').length;
        const failCount = statuses.filter((status) => status === 'FAIL').length;
        const incompleteCount = statuses.filter((status) => status === 'INCOMPLETE').length;
        const pendingReviewCount = statuses.filter((status) => status === 'PENDING_REVIEW').length;
        const pendingCount = statuses.filter((status) => status === 'PENDING_REVIEW' || status === 'INCOMPLETE').length;
        const reportTypeSummary = reports.reduce((acc: Record<string, { reportType: string; status: HeatReportStatus; count: number }>, report: any) => {
          const reportType = String(report.reportType || report.report_type || 'REPORT').replace(/_/g, ' ');
          const status = normalizeHeatReportStatus(report.overallStatus || report.overall_status);
          const key = `${reportType}-${status}`;
          acc[key] = acc[key] || { reportType, status, count: 0 };
          acc[key].count += 1;
          return acc;
        }, {});

        return {
          heatNumber,
          pipeCount: heatPipes.length,
          lotCount: lots.size,
          lots: Array.from(lots),
          reportCount: reports.length,
          passCount,
          failCount,
          incompleteCount,
          pendingReviewCount,
          pendingCount,
          overallStatus,
          reports: Object.values(reportTypeSummary),
          latestDate: heatPipes[0]?.materialDate,
        };
      })
      .sort((a, b) => a.heatNumber.localeCompare(b.heatNumber));
  }, [pipeMaterials, pipeMillReports]);

  const filteredHeatSummaryRows = React.useMemo(() => {
    const search = heatSummarySearch.trim().toLowerCase();
    return heatSummaryRows.filter((row) => {
      const matchesSearch = !search || [
        row.heatNumber,
        ...row.lots,
        row.overallStatus,
      ].some((value) => String(value || '').toLowerCase().includes(search));

      const matchesStatus =
        heatSummaryStatusFilter === 'all' ||
        (heatSummaryStatusFilter === 'NO_REPORTS' ? row.reportCount === 0 : row.reportCount > 0 && row.overallStatus === heatSummaryStatusFilter);

      return matchesSearch && matchesStatus;
    });
  }, [heatSummaryRows, heatSummarySearch, heatSummaryStatusFilter]);

  const selectedHeatPipeMaterials = React.useMemo(
    () => selectedHeatNumber
      ? sortedPipeMaterials.filter((material: MaterialData) => String(material.details?.pipeHeatNumber || 'UNASSIGNED') === selectedHeatNumber)
      : sortedPipeMaterials,
    [selectedHeatNumber, sortedPipeMaterials]
  );
  const selectedReportLotNumbers = React.useMemo(() => {
    if (!selectedMaterial) return [];
    const reportHeatNumber = String(selectedHeatNumber || selectedMaterial.details?.pipeHeatNumber || 'UNASSIGNED');
    return Array.from(new Set(
      sortedPipeMaterials
        .filter((material: MaterialData) => String(material.details?.pipeHeatNumber || 'UNASSIGNED') === reportHeatNumber)
        .map(getPipeLotNumber)
        .filter((lot) => lot && lot !== '-')
    ));
  }, [selectedHeatNumber, selectedMaterial, sortedPipeMaterials]);
  const selectedHeatSummary = React.useMemo(
    () => heatSummaryRows.find((row) => row.heatNumber === selectedHeatNumber),
    [heatSummaryRows, selectedHeatNumber]
  );
  const selectedHeatReports = React.useMemo(
    () => selectedHeatNumber
      ? pipeMillReports.filter((report: any) => String(report.heatNumber || report.heat_number || 'UNASSIGNED') === selectedHeatNumber)
      : [],
    [pipeMillReports, selectedHeatNumber]
  );
  const selectedHeatChemicalReports = React.useMemo(
    () => selectedHeatReports.filter((report: any) => getReportType(report) === 'CHEMICAL'),
    [selectedHeatReports]
  );
  const selectedHeatChemicalStatus = React.useMemo(
    () => combineHeatReportStatuses(selectedHeatChemicalReports.map((report: any) => normalizeHeatReportStatus(report.overallStatus || report.overall_status))),
    [selectedHeatChemicalReports]
  );
  const selectedHeatLotRows = React.useMemo(() => {
    const grouped = selectedHeatPipeMaterials.reduce((acc: Record<string, MaterialData[]>, material: MaterialData) => {
      const lot = getPipeLotNumber(material);
      acc[lot] = [...(acc[lot] || []), material];
      return acc;
    }, {});

    return Object.entries(grouped).map(([lotNumber, lotPipes]) => {
      const reportsForLot = selectedHeatReports.filter((report: any) => getReportLotNumber(report) === lotNumber);
      const savedReports = LOT_LEVEL_REPORTS.map((reportConfig) => {
        const matching = reportsForLot.filter((report: any) => getReportType(report) === reportConfig.key);
        if (!matching.length) return null;
        return {
          ...reportConfig,
          status: combineHeatReportStatuses(matching.map((report: any) => normalizeHeatReportStatus(report.overallStatus || report.overall_status))),
          count: matching.length,
        };
      }).filter((report): report is { key: string; label: string; status: HeatReportStatus; count: number } => Boolean(report));
      return {
        lotNumber,
        pipeCount: lotPipes.length,
        reports: savedReports,
      };
    }).filter((lot) => lot.reports.length > 0);
  }, [selectedHeatPipeMaterials, selectedHeatReports]);
  const selectedHeatBatchRows = React.useMemo(() => {
    const heatTreatReports = selectedHeatReports.filter((report: any) => getReportType(report) === 'HEAT_TREAT');
    if (!heatTreatReports.length) return [];

    const grouped = heatTreatReports.reduce((acc: Record<string, any[]>, report: any) => {
      const batch = getReportBatchNumber(report) || `Batch ${report.id || Object.keys(acc).length + 1}`;
      acc[batch] = [...(acc[batch] || []), report];
      return acc;
    }, {});

    return Object.entries(grouped).map(([batchNumber, reports]) => ({
      batchNumber,
      pipeCount: selectedHeatPipeMaterials.length,
      status: combineHeatReportStatuses(reports.map((report: any) => normalizeHeatReportStatus(report.overallStatus || report.overall_status))),
      count: reports.length,
    }));
  }, [selectedHeatNumber, selectedHeatPipeMaterials.length, selectedHeatReports]);
  const openSelectedHeatReport = React.useCallback((reportType?: string | null) => {
    const firstPipe = selectedHeatPipeMaterials[0];
    if (!firstPipe) return;
    setSelectedMaterial(firstPipe);
    setInitialPipeReportType(reportType || null);
    setIsPipeReportModalOpen(true);
  }, [selectedHeatPipeMaterials]);


  const handleUpload = (files: FileList | null) => {
    // Handle file upload
    refreshMaterials();
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleteLoading(true);
      if (onRowDelete) {
        await onRowDelete(itemToDelete?.materialId, itemToDelete?.materialCategory);
        setIsDeleteModalVisible(false);
        setItemToDelete(null);
        setDeleteLoading(false);
      } else {
        const res = await deleteMaterials([itemToDelete?.materialId], itemToDelete?.materialCategory);
        if (res) {
          setIsDeleteModalVisible(false);
          setItemToDelete(null);
          setDeleteLoading(false);
          refreshMaterials();
        }
      }
    } catch (error) {
      setDeleteLoading(false);
      setIsDeleteModalVisible(false);
      return null;
    }
  }

  const handleUpdateMaterial = async (updatedMaterial: MaterialData) => {
    await refreshMaterials();
  };

  // Handle document operations
  const handleDocumentClick = (material: MaterialData) => {
    setSelectedMaterial(material);
    setIsDocumentModalVisible(true);
  };

  const handleDocumentDownload = (document: any) => {
    // Close the modal first
    setIsDocumentModalVisible(false);
    
    if (onOpenViewer) {
      onOpenViewer(document);
    } else if (document.fileUrl) {
      window.open(document.fileUrl, '_blank');
    }
  };

  const handleDeleteDocument = (doc: any) => {
    setDocumentToDelete(doc);
    setIsDocumentDeleteModalOpen(true);
  };

  const handleCancelDeleteDocument = () => {
    setIsDocumentDeleteModalOpen(false);
    setDocumentToDelete(null);
  };

  const handleConfirmDeleteDocument = async () => {
    if (!documentToDelete) return;

    setDeleteDocumentLoading(true);
    try {
      // Get materialTraceabilityId from the document
      const materialTraceabilityId = documentToDelete.id || documentToDelete.materialTraceabilityId;
      
      if (!materialTraceabilityId) {
        console.error('Material traceability ID not found', documentToDelete);
        setDeleteDocumentLoading(false);
        return;
      }

      const payload = {
        materialDocsIds: [Number(materialTraceabilityId)]
      };

      const deleteResponse = await DeleteMaterialDocument(payload);

      if (deleteResponse) {
        // Refresh materials after successful deletion
        refreshMaterials();
        // Close the delete modal
        handleCancelDeleteDocument();
        // Close the document modal if it's open
        setIsDocumentModalVisible(false);
        setSelectedMaterial(null);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    } finally {
      setDeleteDocumentLoading(false);
    }
  };

  const handleSegmentation = (pipe: MaterialData) => {
    setSelectedPipeForSegmentation(pipe);
    setIsSegmentationModalOpen(true);
  };

  const handleSaveSegments = (segments: any[]) => {
    // TODO: Implement API call to save segments
    refreshMaterials();
  };

  // Helper function to transform documents for the modal
  const transformDocumentsForModal = (material: MaterialData) => {
    const documents = material.documents || [];
    const transformedDocs = documents.flatMap(doc => {
      // Handle the new API structure with materials array
      if ((doc as any).materials && Array.isArray((doc as any).materials)) {
        return (doc as any).materials.map((mat: any) => ({
          id: mat.materialTraceabilityId || Math.random().toString(),
          fileName: mat.fileName,
          fileUrl: mat.fileUrl,
          uploadDate: mat.uploadedAt || mat.createdAt,
          type: mat.tags && mat.tags.length > 0 ? mat.tags.map((tag: any) => tag.tagName?.toLowerCase().replace('_', ' ')).join(', ') : 'document'
        }));
      }
      // Handle legacy structure
      return [{
        id: (doc as any).id || Math.random().toString(),
        fileName: (doc as any).fileName,
        fileUrl: (doc as any).fileUrl,
        uploadDate: (doc as any).uploadedAt,
        type: (doc as any).tags.join(', ')
      }];
    });

    // Sort by uploadDate in descending order (newest first)
    return transformedDocs.sort((a, b) => {
      const dateA = a.uploadDate ? new Date(a.uploadDate).getTime() : 0;
      const dateB = b.uploadDate ? new Date(b.uploadDate).getTime() : 0;
      return dateB - dateA; // Descending order (newest first)
    });
  };

  const renderItemDetails = (item: DeleteItem) => {
    return (
      <div className="text-sm text-gray-700 py-2 border-b border-gray-200 last:border-b-0">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <div>• NRI: {item.details?.pipeNRI}</div>
          <div>• Pipe No: {item.details?.pipeNo}</div>
          <div>• Heat Number: {item.details?.pipeHeatNumber}</div>
          <div>• Length: {item.details?.pipeLength}</div>
        </div>
      </div>
    );
  };


  // Check if there are any specifications for this subtype
  const hasAnyMaterials = pipeMaterials.length > 0;

  // If no specifications exist for any pipes in this subtype, show empty state
  if (!hasActiveFilters && !hasAnyMaterials) {
    return (
      <div className="space-y-6">

        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <FileText className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No pipes created yet</h3>
          <p className="text-gray-500 max-w-sm">
            Start by adding pipes for your pipes project. Click the "Add Material" button to get started.
          </p>
        </div>
      </div>
    );
  }

  //console.log(pipeSizesData.pipeFitter);
  return (
    <>

      <div className="space-y-4">
        {isBasePipeCategory && !selectedHeatNumber && !forceTableView && (
          <Card>
            <CardContent className="p-4">
              <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-[16px] font-semibold text-blueprint-blue">Base pipe heat summary</h3>
                 
                </div>
                <div className="text-[12px] font-semibold text-heritage-grey">
                  {filteredHeatSummaryRows.length} of {heatSummaryRows.length} heats · {pipeMaterials.length} pipes
                </div>
              </div>

              <div className="mb-4 grid gap-3 md:grid-cols-[1fr_220px_auto]">
                <div>
                  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.06em] text-heritage-grey">
                    Search
                  </label>
                  <input
                    value={heatSummarySearch}
                    onChange={(event) => setHeatSummarySearch(event.target.value)}
                    placeholder="Search heat number or lot number..."
                    className="h-10 w-full rounded-lg border border-[#E2E5EA] bg-white px-3 text-[13px] text-text-grey outline-none focus:ring-2 focus:ring-hero-red-10"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.06em] text-heritage-grey">
                    Report Status
                  </label>
                  <select
                    value={heatSummaryStatusFilter}
                    onChange={(event) => setHeatSummaryStatusFilter(event.target.value as 'all' | HeatReportStatus | 'NO_REPORTS')}
                    className="h-10 w-full rounded-lg border border-[#E2E5EA] bg-white px-3 text-[13px] text-text-grey outline-none focus:ring-2 focus:ring-hero-red-10"
                  >
                    <option value="all">All statuses</option>
                    <option value="PASS">Pass</option>
                    <option value="FAIL">Fail</option>
                    <option value="PENDING_REVIEW">Pending Review</option>
                    <option value="INCOMPLETE">Incomplete</option>
                    <option value="NO_REPORTS">No Reports</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setHeatSummarySearch('');
                      setHeatSummaryStatusFilter('all');
                    }}
                    className="h-10"
                  >
                    Reset
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <Table className="min-w-[900px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Heat Number</TableHead>
                      <TableHead className="text-center">Pipe Count</TableHead>
                      <TableHead className="text-center">Lot Count</TableHead>
                      <TableHead>Lot Numbers</TableHead>
                      <TableHead className="text-center">Reports</TableHead>
                      <TableHead className="text-center">Report Status</TableHead>
                      <TableHead className="text-center">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredHeatSummaryRows.length > 0 ? filteredHeatSummaryRows.map((row) => (
                      <TableRow
                        key={row.heatNumber}
                        className="cursor-pointer hover:bg-page-bg"
                        onClick={() => setSelectedHeatNumber(row.heatNumber)}
                      >
                        <TableCell>
                          <div className="font-semibold text-blueprint-blue">{row.heatNumber}</div>
                          <div className="text-[11px] text-heritage-grey">
                            {row.latestDate ? `Latest pipe: ${formatDate(row.latestDate)}` : 'No date'}
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-semibold">{row.pipeCount}</TableCell>
                        <TableCell className="text-center font-semibold">{row.lotCount}</TableCell>
                        <TableCell>
                          <div className="flex max-w-[360px] flex-wrap gap-1">
                            {row.lots.length > 0 ? row.lots.slice(0, 5).map((lot) => (
                              <span key={lot} className="rounded-full bg-[#F4F6F8] px-2 py-1 text-[11px] text-blueprint-blue">
                                {lot}
                              </span>
                            )) : <span className="text-heritage-grey">-</span>}
                            {row.lots.length > 5 && (
                              <span className="rounded-full bg-[#F4F6F8] px-2 py-1 text-[11px] text-heritage-grey">
                                +{row.lots.length - 5}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="font-semibold">{row.reportCount}</div>
                          <div className="text-[11px] text-heritage-grey">
                            {row.passCount} pass · {row.pendingCount} pending
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span
                            className={cn(
                              'rounded-full px-2 py-1 text-[11px] font-semibold uppercase',
                              row.overallStatus === 'PASS' && 'bg-green-50 text-green-700',
                              row.overallStatus === 'FAIL' && 'bg-red-50 text-red-700',
                              row.overallStatus === 'PENDING_REVIEW' && 'bg-amber-50 text-amber-700',
                              row.overallStatus === 'INCOMPLETE' && 'bg-gray-100 text-gray-700',
                            )}
                          >
                            {row.reportCount ? row.overallStatus.replace(/_/g, ' ') : 'No Reports'}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectedHeatNumber(row.heatNumber);
                            }}
                          >
                           <EyeIcon className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={7} className="py-8 text-center text-heritage-grey">
                          {isHeatSummaryLoading ? 'Loading heat summary...' : heatSummaryRows.length ? 'No heat summary matches the current filters.' : 'No heat summary available.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {isBasePipeCategory && selectedHeatNumber && !forceTableView && (
          <div className="space-y-3">
            <div className="rounded-lg border border-[#E2E5EA] bg-white">
              <div className="flex items-start justify-between gap-4 border-b border-[#EEF0F3] px-4 py-3">
                <div className="flex items-start gap-3">
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setSelectedHeatNumber(null)}>
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-heritage-grey">Heat</div>
                    <div className="text-[18px] font-semibold text-blueprint-blue">{selectedHeatNumber}</div>
                    <div className="mt-1 text-[12px] text-heritage-grey">
                      {selectedHeatSummary?.pipeCount || selectedHeatPipeMaterials.length} pipes in heat
                      {selectedHeatSummary?.lotCount ? ` - ${selectedHeatSummary.lotCount} lots` : ''}
                      {selectedHeatSummary?.latestDate ? ` - latest pipe ${formatDate(selectedHeatSummary.latestDate)}` : ''}
                    </div>
                  </div>
                </div>
                <span className={cn('rounded-full px-2 py-1 text-[11px] font-semibold uppercase', selectedHeatSummary?.reportCount ? getStatusPillClass(selectedHeatSummary.overallStatus) : 'bg-gray-100 text-gray-700')}>
                  {selectedHeatSummary?.reportCount ? selectedHeatSummary.overallStatus.replace(/_/g, ' ') : 'No Reports'}
                </span>
              </div>

              <div className="grid gap-4 px-4 py-3 md:grid-cols-5">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-heritage-grey">Chemistry</div>
                  <span className={cn('mt-1 inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold uppercase', selectedHeatChemicalReports.length ? getStatusPillClass(selectedHeatChemicalStatus) : 'border-gray-200 bg-gray-50 text-gray-700')}>
                    {selectedHeatChemicalReports.length ? selectedHeatChemicalStatus.replace(/_/g, ' ') : 'No Report'}
                  </span>
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-heritage-grey">Pipes In Heat</div>
                  <div className="mt-1 text-[18px] font-semibold text-blueprint-blue">{selectedHeatSummary?.pipeCount || selectedHeatPipeMaterials.length}</div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-heritage-grey">Lots</div>
                  <div className="mt-1 text-[18px] font-semibold text-blueprint-blue">{selectedHeatSummary?.lotCount || 0}</div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-heritage-grey">Reports Done</div>
                  <div className="mt-1 text-[18px] font-semibold text-blueprint-blue">{selectedHeatSummary?.reportCount || 0}</div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-heritage-grey">Pending / Incomplete</div>
                  <div className="mt-1 text-[18px] font-semibold text-amber-700">{selectedHeatSummary?.pendingCount || 0}</div>
                </div>
              </div>
            </div>

            {selectedHeatChemicalReports.length > 0 && (
            <div>
              <div className="mb-2 flex items-center gap-2">
                <span className="rounded bg-hero-red px-2 py-1 text-[10px] font-semibold text-white">HEAT</span>
                <span className="text-[13px] font-semibold text-blueprint-blue">Heat-level reports</span>
                <span className="text-[11px] text-heritage-grey">One record per heat - covers every pipe cast from it</span>
              </div>
              <button
                type="button"
                onClick={() => openSelectedHeatReport('CHEMICAL')}
                className="flex w-full items-center justify-between rounded-lg border border-[#E2E5EA] bg-white px-4 py-3 text-left hover:bg-page-bg"
              >
                <div className="grid flex-1 gap-2 md:grid-cols-[1fr_1fr_1fr]">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-heritage-grey">Chemical Analysis</div>
                    <div className="font-semibold text-blueprint-blue">Heat {selectedHeatNumber}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-heritage-grey">Coverage</div>
                    <div className="text-[12px] text-heritage-grey">Ladle analysis - {selectedHeatSummary?.pipeCount || selectedHeatPipeMaterials.length} pipes covered</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-heritage-grey">Metric</div>
                    <div className="font-mono text-[12px] text-blueprint-blue">CEQ from Chemical report</div>
                  </div>
                </div>
                <span className={cn('ml-3 rounded-full border px-2 py-1 text-[11px] font-semibold uppercase', getStatusPillClass(selectedHeatChemicalStatus))}>
                  {selectedHeatChemicalStatus.replace(/_/g, ' ')}
                </span>
                <span className="ml-3 text-heritage-grey">{'>'}</span>
              </button>
            </div>
            )}

            {selectedHeatLotRows.length > 0 && (
            <div>
              <div className="mb-2 flex items-center gap-2">
                <span className="rounded bg-sky-600 px-2 py-1 text-[10px] font-semibold text-white">LOT</span>
                <span className="text-[13px] font-semibold text-blueprint-blue">Lot-level reports</span>
                <span className="text-[11px] text-heritage-grey">{selectedHeatLotRows.length} lot{selectedHeatLotRows.length === 1 ? '' : 's'} - each lot has mechanical tests</span>
              </div>
              <div className="space-y-2">
                {selectedHeatLotRows.map((lot) => (
                  <div key={lot.lotNumber} className="rounded-lg border border-[#E2E5EA] bg-white px-4 py-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="min-w-[160px]">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-heritage-grey">Lot</div>
                        <div className="font-semibold text-blueprint-blue">{lot.lotNumber}</div>
                        <div className="text-[11px] text-heritage-grey">{lot.pipeCount} pipes</div>
                      </div>
                      <div className="flex flex-1 flex-wrap gap-2">
                        {lot.reports.map((report) => (
                          <button
                            key={`${lot.lotNumber}-${report.key}`}
                            type="button"
                            onClick={() => openSelectedHeatReport(report.key)}
                            className={cn('rounded-md border px-3 py-1 text-[12px] font-semibold hover:opacity-80', getStatusPillClass(report.status))}
                          >
                            {report.label} {report.count ? `(${report.count})` : ''}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}

            {selectedHeatBatchRows.length > 0 && (
            <div>
              <div className="mb-2 flex items-center gap-2">
                <span className="rounded bg-amber-500 px-2 py-1 text-[10px] font-semibold text-white">BATCH</span>
                <span className="text-[13px] font-semibold text-blueprint-blue">Furnace batch reports</span>
                <span className="text-[11px] text-heritage-grey">One heat-treatment record per furnace charge</span>
              </div>
              <div className="space-y-2">
                {selectedHeatBatchRows.map((batch) => (
                  <button
                    key={batch.batchNumber}
                    type="button"
                    onClick={() => openSelectedHeatReport('HEAT_TREAT')}
                    className="flex w-full items-center justify-between rounded-lg border border-[#E2E5EA] bg-white px-4 py-3 text-left hover:bg-page-bg"
                  >
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-heritage-grey">Heat Treatment</div>
                      <div className="font-semibold text-blueprint-blue">{batch.batchNumber}</div>
                    </div>
                    <div className="hidden text-[12px] text-heritage-grey md:block">Furnace charge - {batch.pipeCount} pipes</div>
                    <span className={cn('rounded-full border px-2 py-1 text-[11px] font-semibold uppercase', getStatusPillClass(batch.status))}>
                      {batch.status.replace(/_/g, ' ')}
                    </span>
                    <span className="text-heritage-grey">{'>'}</span>
                  </button>
                ))}
              </div>
            </div>
            )}
          </div>
        )}

        {(!isBasePipeCategory || selectedHeatNumber || forceTableView) && (
          <>
        {/* Material Filters */}
        <PipeFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filters={filters}
          onFilterChange={onFilterChange}
          uniquePipeNumbers={uniquePipeNumbers}
          uniquePipeNRIs={uniquePipeNRIs}
          uniquePipeProjectStages={uniquePipeProjectStages}
          uniqueHeatNumbers={uniqueHeatNumbers}
          uniqueOuterDiameters={uniqueOuterDiameters}
          uniqueWallThicknesses={uniqueWallThicknesses}
          uniqueProjectNames={uniqueProjectNames}
          hasActiveFilters={hasActiveFilters}
          filteredCount={pipeMaterials.length}
          onResetFilters={onResetFilters}
        />

        <Card>
          <CardContent className="p-4">
            <div className="overflow-x-auto">
              <div className="overflow-y-auto max-h-[400px] md:max-h-[500px] lg:max-h-[calc(100vh-350px)]">
                <Table className="min-w-[1300px]" containerClassName="">
                  <TableHeader className="sticky top-0 z-20 bg-white">
                  <TableRow>
                    <SortableTableHead
                      sortKey="details.pipeNRI"
                      onSort={handleSort}
                      sortDirection={getSortDirection("details.pipeNRI")}
                    >
                      NRI
                    </SortableTableHead>
                    <SortableTableHead
                      sortKey="details.pipeNo"
                      onSort={handleSort}
                      sortDirection={getSortDirection("details.pipeNo")}
                    >
                      Pipe No
                    </SortableTableHead>
                    <SortableTableHead
                      sortKey="details.pipePipeId"
                      onSort={handleSort}
                      sortDirection={getSortDirection("details.pipePipeId")}
                    >
                      Pipe Id
                    </SortableTableHead>
                    <SortableTableHead
                      sortKey="details.pipeHeatNumber"
                      onSort={handleSort}
                      sortDirection={getSortDirection("details.pipeHeatNumber")}
                    >
                      Heat Number
                    </SortableTableHead>
                    <SortableTableHead
                      sortKey="details.pipeLength"
                      onSort={handleSort}
                      sortDirection={getSortDirection("details.pipeLength")}
                      className="text-center"
                    >
                      Length ({unit})
                    </SortableTableHead>
                    <SortableTableHead
                      sortKey="details.pipeProjectStage"
                      onSort={handleSort}
                      sortDirection={getSortDirection("details.pipeProjectStage")}
                      className="text-center"
                    >
                      Project Stage
                    </SortableTableHead>
                    <SortableTableHead
                      sortKey="materialStatus"
                      onSort={handleSort}
                      sortDirection={getSortDirection("materialStatus")}
                      className="text-center"
                    >
                      Status
                    </SortableTableHead>
                    <SortableTableHead
                      sortKey="materialDate"
                      onSort={handleSort}
                      sortDirection={getSortDirection("materialDate")}
                      className="text-center w-[100px]"
                    >
                      Date
                    </SortableTableHead>
                    {/* <SortableTableHead
                      sortKey="assemblyNumbers"
                      onSort={handleSort}
                      sortDirection={getSortDirection("assemblyNumbers")}
                      className="text-center"
                    >
                      Assembly Number
                    </SortableTableHead> */}
                    {hasPipeFitter &&
                      <TableHead className="text-center">Pipe Data</TableHead>}
                    <TableHead className="text-center">Documents</TableHead>
                    <TableHead className="text-center whitespace-nowrap sticky right-0 bg-white">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedHeatPipeMaterials.length > 0 ? selectedHeatPipeMaterials
                    .slice((currentPage - 1) * materialsPerPage, currentPage * materialsPerPage)
                    .map((pipe: MaterialData) => {
                      const statusDisplay = getMaterialStatusDisplay(pipe);
                      return (
                      <TableRow   key={pipe.materialId}>
                        <TableCell>{pipe.details.pipeNRI}</TableCell>
                        <TableCell>{pipe.details.pipeNo}</TableCell>
                        <TableCell>{pipe.details?.pipePipeId || '-'}</TableCell>
                        <TableCell>{pipe.details?.pipeHeatNumber || '-'}</TableCell>
                        <TableCell className="text-center">{convertValueFromInches(pipe.details.pipeLength, unit)}</TableCell>
                        {/* <TableCell className="text-center">{pipe.details.pipeGrade}</TableCell>
                  <TableCell className="text-center">{pipe.details.materialType}</TableCell> */}
                        <TableCell className="text-center">{pipe.details.pipeProjectStage}</TableCell>
                        <TableCell className="text-center">
                          <span
                            className={cn(
                              'px-2 py-1 text-xs rounded-full',
                              statusDisplay === 'Available' && 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
                              statusDisplay === 'Consumed' && 'bg-gray-100 text-gray-800',
                              statusDisplay === 'Shipped to UTQ' && 'text-[#B38600] bg-[rgba(230,161,0,0.08)]'
                            )}
                          >
                            {statusDisplay}
                          </span>
                        </TableCell>
                        <TableCell className="text-center w-[150px]">{pipe.materialDate ? formatDate(pipe.materialDate) : '-'}</TableCell>
                          {/* <TableCell className="text-center">{pipe.assemblyNumbers && pipe.assemblyNumbers.length > 0 ? pipe.assemblyNumbers?.join(', ') || '-' : '-'}</TableCell> */}
                          {hasPipeFitter && <TableCell className="text-center">
                          <div className={`flex items-center gap-2 justify-center`}>

                            {/* View Pipe Data Button - only show if pipe data exists */}
                            {pipe.details?.pipeExtraDetails?.pipeData && Object.keys(pipe.details.pipeExtraDetails.pipeData).length > 0 && (
                              <Button
                                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                                onClick={() => {
                                  setSelectedMaterial(pipe);
                                  setIsPipeFitterOpen(true);
                                }}
                                title="View Pipe Data"
                                aria-label="View Pipe Data"
                                variant="outline"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}


                            <Button
                              className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                              onClick={() => {
                                setSelectedMaterial(pipe);
                                setIsPipeDataUploadModalOpen(true);
                              }}
                              title={`Upload Pipefitter documents`}
                              aria-label={`Upload Pipefitter  documents`}
                              variant="outline"
                              disabled={!canEdit}
                            >
                              <Upload className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>}

                        <TableCell className="text-center w-[80px]">
                          <div className={`flex items-center gap-2 ${!pipe.documents || pipe.documents.length === 0 ? 'justify-center' : ''}`}>

                            {pipe.documents && pipe.documents.length > 0 &&
                              <Button
                                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                                title="View Documents"
                                variant="outline"
                                onClick={() => handleDocumentClick(pipe)}
                              >
                                <FileText className="h-4 w-4" />
                                {pipe.documents.length}
                              </Button>
                            }


                            <Button
                              className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                              onClick={() => {
                                setSelectedMaterial(pipe);
                                setIsUploadModalOpen(true);
                              }}
                              title={`Upload ${pipe.materialCategory}`}
                              aria-label={`Upload ${pipe.materialCategory}`}
                              variant="outline"
                              disabled={!canEdit}
                            >
                              <Upload className="h-4 w-4" />
                            </Button>

                          </div>
                        </TableCell>
                        <TableCell className="text-center w-[80px] sticky right-0 z-10 bg-white">
                          <div className="flex items-center gap-2 justify-center">


                          <Button
                              onClick={() => {
                                setSelectedMaterial(pipe);
                                setInitialPipeReportType(null);
                                setIsPipeReportModalOpen(true);
                              }}
                              className="text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                              title="View Full Report"
                              variant="outline"
                              disabled={deletingRows?.has(String(pipe.materialId))}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => {
                                setItemToUpdate(pipe);
                                setIsUpdateModalVisible(true);
                              }}
                              className="text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                              title="Edit Material"
                              variant="outline"
                              disabled={!canEdit || deletingRows?.has(String(pipe.materialId))}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>

                            <Button
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 w-8 h-8 p-0 flex items-center justify-center"
                              onClick={() => {
                                setIsDeleteModalVisible(true);

                                setItemToDelete(pipe)
                              }}
                              title="Delete Material"
                              variant="outline"
                              disabled={!canEdit || deletingRows?.has(String(pipe.materialId))}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>

                            {pipe.details.pipeProjectStage === 'PQT' && (
                              <Button
                                onClick={() => handleSegmentation(pipe)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50 w-8 h-8 p-0 flex items-center justify-center"
                                title="Segmentation"
                                variant="outline"
                                disabled={!canEdit || deletingRows?.has(String(pipe.materialId))}
                              >
                                <Split className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );}) : (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-gray-500">No pipes found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
            </div>
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalItems={selectedHeatPipeMaterials.length}
              itemsPerPage={materialsPerPage}
              onPageChange={onPageChange}
              onItemsPerPageChange={onItemsPerPageChange}
              pageSizeOptions={[10, 25, 50, 100]}
              showPageNumbers={true}
              maxVisiblePages={5}
            />
          </CardContent>
        </Card>
        </>
        )}
        <DeleteConfirmationDialog
          isOpen={isDeleteModalVisible}
          onClose={() => setIsDeleteModalVisible(false)}
          onConfirm={handleConfirmDelete}
          itemType="Material"
          itemName={itemToDelete?.details?.pipeNRI}
          //itemName={selectedPipeSize.}
          itemToDelete={itemToDelete}
          detailsRenderer={renderItemDetails}
          isLoading={deleteLoading}
        />
        {isUpdateModalVisible && <UpdateMaterialDialog
          isOpen={isUpdateModalVisible}
          onClose={() => setIsUpdateModalVisible(false)}
          onUpdate={handleUpdateMaterial}
          material={itemToUpdate}
          project={pipeSizesData}
          unit={unit}
          documentTags={documentTags}
          setDocumentTags={setDocumentTags}
          selectedPipe={pipeSizesData}
        />}

        {/* Document Download Modal */}
        {selectedMaterial && (
          <DocumentDownloadModal
            isOpen={isDocumentModalVisible}
            onClose={() => {
              setIsDocumentModalVisible(false);
              setSelectedMaterial(null);
            }}
            documents={transformDocumentsForModal(selectedMaterial)}
            onDownload={handleDocumentDownload}
            onDelete={handleDeleteDocument}
            title="Pipe Material Documents"
            canDelete={canEdit}
          />
        )}
        {/* Document Upload Modal */}
        <MaterialDocumentUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onUpload={handleUpload}
          title="Upload Material Document"
          acceptedTypes=".pdf,.doc,.docx,.xlsx,.xls"
          multiple={true}
          maxFiles={1}
          buttonText="Select Document"
          label="Document"
          description="Upload material documents (PDF, Excel, DOC, etc...)"
          material={selectedMaterial || undefined}
          documentTags={documentTags}
          setDocumentTags={setDocumentTags}
          materialType="Pipe"
          selectedPipeSize={pipeSizesData}
        />

        {/* Pipe Segmentation Modal */}
        {isSegmentationModalOpen && selectedPipeForSegmentation && (
          <PipeSegmentationModal
            isOpen={isSegmentationModalOpen}
            onClose={() => {
              setIsSegmentationModalOpen(false);
              setSelectedPipeForSegmentation(null);
            }}
            pipeId={selectedPipeForSegmentation.details.pipePipeId || 'N/A'}
            heatNumber={selectedPipeForSegmentation.details.pipeHeatNumber || 'N/A'}
            pipeNo={selectedPipeForSegmentation.details.pipeNo || 'N/A'}
            onSave={handleSaveSegments}
            selectedMaterial={selectedPipeForSegmentation}
          />
        )}

        {isPipeFitterOpen && selectedMaterial && <PipeFfitter
          isOpen={isPipeFitterOpen}
          isPipeFitterOpen={false}
          onClose={() => setIsPipeFitterOpen(false)}
          selectedMaterial={selectedMaterial}
          selectedPipe={pipeSizesData}
          project={pipeSizesData}
          unit={'mm'}
        />}

        {isPipeDataUploadModalOpen && <PipeDataUploadModal
          isOpen={isPipeDataUploadModalOpen}
          onClose={() => {
            setIsPipeDataUploadModalOpen(false);
            setSelectedMaterial(null);
          }}
          selectedMaterial={selectedMaterial ?? undefined}
          selectedPipe={pipeSizesData}
          project={project}
          refreshMaterials={refreshMaterials}
        />}

        {isPipeReportModalOpen && selectedMaterial && (
          <PipeMaterialReportModal
            isOpen={isPipeReportModalOpen}
            onClose={() => {
              setIsPipeReportModalOpen(false);
              setSelectedMaterial(null);
              setInitialPipeReportType(null);
            }}
            selectedMaterial={selectedMaterial}
            selectedPipe={pipeSizesData}
            unit={unit}
            availableLotNumbers={selectedReportLotNumbers}
            onReportsChanged={handlePipeReportChanged}
            initialReportType={initialPipeReportType}
          />
        )}

        {/* Document Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          isOpen={isDocumentDeleteModalOpen}
          onClose={handleCancelDeleteDocument}
          onConfirm={handleConfirmDeleteDocument}
          isLoading={deleteDocumentLoading}
          itemToDelete={documentToDelete}
          itemType="Material Document"
          itemName={documentToDelete?.fileName || documentToDelete?.file_name || ''}
          title="Delete Material Document"
        />
        
      </div>
    </>
  );
}); 